import { unionBounds } from '@/utils/bounds-kit';
import type { Bounds } from '@/version-types';
import { ITALIC_SKEW_DEG, MAX_STROKE_COUNT } from '../../constants';
import type { TextRenderInput } from '../../types';
import { getPathBounds, transformBounds } from '../../utils/bounds';
import { findSpanIndex } from '../../utils/snapshot';
import type { RenderContext } from '../context';
import type { GlyphItem, GlyphStroke } from './draw';

export { drawGlyphs, drawStrokes } from './draw';
export type { GlyphItem, GlyphStroke } from './draw';
export { createInstructionNode, wrapTransform } from './nodes';

/**
 * 当前一轮正文与字符 marker 共同消费的字形和描边事实。
 */
export type GlyphVisual = Readonly<{
    items: GlyphItem[];
    strokes: GlyphStroke[];
}>;

/**
 * 聚合正文 path 与装饰线矩形的视觉边界。
 *
 * 每个 GlyphItem 已携带 `resolveGlyph()` 在本轮按唯一 path 解析的原始 bounds；这里不再调用
 * SVG path 边界解析，只复刻节点创建时的 fake italic、竖排旋转和 Y 翻转顺序，再合并到输出坐标系。
 *
 * @param items - 当前 render 的有序字形流。
 * @returns 至少包含一个非空 path 或有效装饰线矩形时的合并边界，否则返回 null。
 */
export const unionGlyphBounds = (items: readonly GlyphItem[]): Bounds | null =>
    items.reduce<Bounds | null>((bounds, item) => {
        const { glyph, pathBounds } = item;
        if (glyph.kind === 'decorationRect') {
            if (glyph.width <= 0 || glyph.height <= 0) return bounds;
            return unionBounds(bounds, {
                left: glyph.x,
                top: glyph.y,
                right: glyph.x + glyph.width,
                bottom: glyph.y + glyph.height,
            });
        }
        if (glyph.kind !== 'path' || !pathBounds) return bounds;
        const centerY = (pathBounds.top + pathBounds.bottom) / 2;
        const transformed = transformBounds(pathBounds, (x, y) => {
            const skewedX = x + (glyph.fakeItalic ? Math.tan((ITALIC_SKEW_DEG * Math.PI) / 180) * y : 0);
            return glyph.orientation === 'rotated90Cw'
                ? { x: glyph.x + y - centerY, y: glyph.y + skewedX }
                : { x: glyph.x + skewedX, y: glyph.y - y };
        });
        return unionBounds(bounds, transformed);
    }, null);

/**
 * 为当前一轮准备带 span 归属的有序字形流和累计描边。
 *
 * 本轮内部使用 `Map<pathData, Bounds>`，相同轮廓无论出现多少次都只执行一次 SVG path 边界解析。
 * 解析出的原始 bounds 附加到 GlyphItem，后续 geometry、竖排旋转中心、描边和 fill mask 共用同一份事实；
 * Map 不跨 render 持久化，避免额外失效协议。
 *
 * @param context - 当前一轮不变的输入、snapshot 与 SVG 命名空间。
 * @param transforms - 按 span 索引声明的花字旋转。
 * @returns 与引擎指令顺序一致的字形及累计描边事实。
 */
export const resolveGlyph = (context: RenderContext, transforms?: ReadonlyMap<number, string>): GlyphVisual => {
    const { snapshot } = context;
    const boundsByPath = new Map<string, Bounds>();
    const resolvePathBounds = (path: string) => {
        const cached = boundsByPath.get(path);
        if (cached) return cached;
        const bounds = getPathBounds(path);
        boundsByPath.set(path, bounds);
        return bounds;
    };
    let glyphCursor = 0;
    const items: GlyphItem[] = snapshot.instructions.map((instruction) => {
        const byteStart =
            instruction.kind === 'delegated' || instruction.kind === 'decorationRect'
                ? instruction.byteStart
                : snapshot.glyphInfos[glyphCursor]?.start;
        const spanIndex = byteStart === undefined ? -1 : findSpanIndex(snapshot.spanRanges, byteStart);
        const item = {
            glyph: instruction,
            spanIndex,
            transform: spanIndex < 0 ? undefined : transforms?.get(spanIndex),
            pathBounds:
                instruction.kind === 'path' && instruction.d.trim() ? resolvePathBounds(instruction.d) : undefined,
        };
        if (instruction.kind !== 'decorationRect') glyphCursor += 1;
        return item;
    });
    return { items, strokes: resolveStrokes(context.input) };
};

/**
 * 解析当前输入最多三层可见描边，并保持既有累计宽度语义。
 *
 * @param input - 当前完整 Text Render Input。
 * @returns 按声明顺序排列、宽度已累计的描边事实。
 */
const resolveStrokes = (input: TextRenderInput): GlyphStroke[] => {
    let accumulatedWidth = 0;
    return (input.strokes ?? [])
        .slice(0, MAX_STROKE_COUNT)
        .filter((stroke) => !stroke.hidden && stroke.width > 0)
        .map((stroke) => {
            accumulatedWidth += stroke.width;
            return { color: stroke.color, width: accumulatedWidth };
        });
};
